import { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconHelp, IconX } from '@tabler/icons-react';
import MDialog from '@/components/ui/atoms/MDialog';
import { MButton } from '@/components/ui/atoms/MButtons';
import { MWizard } from './MWizard';
import { hasSeenGlobalTour, markGlobalTourAsSeen } from '@/utils/storage-utils';
import styles from './MTourTrigger.module.css';

interface MTourTriggerProps {
  page: string; // Wizard page name
}

export interface MTourTriggerRef {
  open: () => void;
}

const TOUR_SEEN_KEY = 'tour_seen_app';
const TOUR_MODAL_COUNT_KEY = 'tour_modal_count';
const MAX_MODAL_SHOWS = 2;

export const MTourTrigger = forwardRef<MTourTriggerRef, MTourTriggerProps>(({ page }, ref) => {
  const { t } = useTranslation();
  const [showTourModal, setShowTourModal] = useState(false);
  const [startWizard, setStartWizard] = useState(false);

  const getModalCount = (): number => {
    const count = localStorage.getItem(TOUR_MODAL_COUNT_KEY);
    return count ? parseInt(count, 10) : 0;
  };

  const incrementModalCount = (): void => {
    const currentCount = getModalCount();
    localStorage.setItem(TOUR_MODAL_COUNT_KEY, (currentCount + 1).toString());
  };

  const canShowModal = (): boolean => {
    return getModalCount() < MAX_MODAL_SHOWS;
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem(TOUR_SEEN_KEY);
    
    if (!hasSeenTour && canShowModal()) {
      setShowTourModal(true);
      incrementModalCount();
      localStorage.setItem(TOUR_SEEN_KEY, 'true');
    }
  }, []);

  useImperativeHandle(ref, () => ({
    open: () => {
      if (canShowModal()) {
        setShowTourModal(true);
        incrementModalCount();
      } else {
        setStartWizard(true);
      }
    },
  }));

  const handleStartTour = () => {
    setShowTourModal(false);
    setStartWizard(true);
  };

  return (
    <>
      {/* Tour Modal */}
      <MDialog
        isOpen={showTourModal}
        onClose={() => setShowTourModal(false)}
        title=""
        primaryCtaText=""
        onPrimaryCta={() => {}}
        showPrimaryCta={false}
        showCloseButton={false}
        disableEscapeKey={true}
        disableOutsideClick={true}
      >
        <div className={styles.tourModalRoot}>
          <button className={styles.tourCloseBtn} onClick={() => {
            setShowTourModal(false);
          }}>
            <IconX size={20} />
          </button>
          <div className={styles.tourContent}>
            <div className={styles.tourIconWrapper}>
              <IconHelp size={64} color="#45168C" stroke={2} />
            </div>
            <h2 className={styles.tourTitle}>{t('wizard.tour.title')}</h2>
            <p className={styles.tourDescription}>
              {t('wizard.tour.description')}
            </p>
            <MButton
              text={t('wizard.tour.startButton')}
              size="md"
              onClick={handleStartTour}
              className={styles.tourButton}
            />
            <MButton
              variant="outline"
              text={t('wizard.tour.laterButton')}
              size="md"
              onClick={() => {
                setShowTourModal(false);
              }}
              className={styles.tourButton}
            />
          </div>
        </div>
      </MDialog>

      {/* Wizard */}
      {startWizard && (
        <MWizard 
          page={page}
          onComplete={() => setStartWizard(false)}
        />
      )}
    </>
  );
});

MTourTrigger.displayName = 'MTourTrigger';
